import React, { useState } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

import { noop } from '@utils';
import Icon from '../Icon';
import IconButton from '../IconButton';
import Modal from '../Modal';

const isProduction = process.env.NODE_ENV === 'production';

interface DefaultFallbackProps {
	error: Error;
	componentStack?: string;
	context: string;
	fallbackRoute?: string | null;
}

interface ErrorBoundaryProps {
	context?: string;
	onReset?: (...args: unknown[]) => void;
	onError?: (error: Error, componentStack: string, context: string) => void;
	fallbackComponent?: React.ComponentType<DefaultFallbackProps>;
	children: React.ReactElement;
	fallbackRoute?: string | null;
	isPage?: boolean;
}

const DefaultFallback = ({ error, context }) => {
	const [showDetails, setShowDetails] = useState(false);
	const title = `Something went wrong${!isProduction && ` in ${context}`}.`;
	const subtitle = `Sorry, something went wrong there. Try again.`;
	return (
		<div className="ErrorFallback h-full w-full bg-primary-dark" role="alert">
			<p className="text-xl text-primary-light">{title}</p>
			<p className="text-base text-primary-light">{subtitle}</p>
			{!isProduction && (
				<div className="font-mono mt-5 space-y-2 rounded-md bg-secondary-dark p-5">
					<p className="text-primary-light">Context: {context}</p>
					<p className="text-primary-light">Error Message: {error.message}</p>

					<IconButton
						id="btn-stack-trace"
						variant="contained"
						color="inherit"
						size="initial"
						className="text-primary-active"
						onClick={() => setShowDetails(!showDetails)}
					>
						<React.Fragment>
							<div>{'Stack Trace'}</div>
							<Icon width="15px" height="15px" name="chevron-down" />
						</React.Fragment>
					</IconButton>

					{showDetails && <p className="px-4 text-primary-light">Stack: {error.stack}</p>}
				</div>
			)}
		</div>
	);
};

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
	context = 'SAOLA',
	onReset = noop,
	onError = noop,
	fallbackComponent: FallbackComponent = DefaultFallback,
	children,
	fallbackRoute = null,
	isPage,
	...rest
}) => {
	const [isOpen, setIsOpen] = useState(true);

	const onErrorHandler = (error, componentStack) => {
		console.error(`${context} Error Boundary`, error, componentStack, context);
		onError(error, componentStack, context);
	};

	const onResetHandler = (...args) => onReset(...args);

	return (
		<ReactErrorBoundary
			fallbackRender={(props) =>
				isPage ? (
					<FallbackComponent {...props} context={context} fallbackRoute={fallbackRoute} />
				) : (
					<Modal
						closeButton
						shouldCloseOnEsc
						isOpen={isOpen}
						title={'Something went wrong'}
						onClose={() => {
							setIsOpen(false);
							if (fallbackRoute && typeof window !== 'undefined') {
								// chuyển hướng đến fallbackRoute
								window.location.href = fallbackRoute;
							}
						}}
					>
						<FallbackComponent {...props} context={context} fallbackRoute={fallbackRoute} />
					</Modal>
				)
			}
			onReset={onResetHandler}
			onError={onErrorHandler}
		>
			{React.cloneElement(children, { ...rest })}
		</ReactErrorBoundary>
	);
};

export default ErrorBoundary;
